"use client";

import { useEffect, useRef, useState } from "react";
import { Check, Moon, Palette, Sun } from "lucide-react";
import { applyTheme, readTheme, THEME_CHANGE_EVENT, type PanelTheme } from "@/lib/theme";

// انتخابِ تمِ پنل: تیره، روشن یا «سایت بایر» (هم‌شکلِ سایتِ عمومی). دکمه آیکنِ تمِ فعلی را
// نشان می‌دهد و با زدنش فهرستِ سه تم باز می‌شود. تا پیش از mount مقدارِ واقعی معلوم نیست،
// پس تا آن لحظه آیکن رندر نمی‌شود تا با HTMLِ سرور ناسازگاری پیش نیاید.
const OPTIONS: Array<{ value: PanelTheme; label: string; hint: string; Icon: typeof Sun }> = [
  { value: "dark", label: "تیره", hint: "حالت پیش‌فرض پنل", Icon: Moon },
  { value: "light", label: "روشن", hint: "زمینه‌ی روشن", Icon: Sun },
  { value: "bayer", label: "سایت بایر", hint: "مثلِ سایت عمومی شرکت", Icon: Palette },
];

export default function ThemeToggle({ className = "", variant = "solid" }: { className?: string; variant?: "solid" | "ghost" }) {
  const [theme, setTheme] = useState<PanelTheme | null>(null);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setTheme(readTheme());
    const onChange = (event: Event) => setTheme((event as CustomEvent<PanelTheme>).detail);
    window.addEventListener(THEME_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(THEME_CHANGE_EVENT, onChange);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = OPTIONS.find((option) => option.value === theme);
  const CurrentIcon = current?.Icon;

  return (
    <div ref={rootRef} className={"relative shrink-0 " + className}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={
          variant === "ghost"
            ? // هم‌شکلِ بقیه‌ی دکمه‌های آیکنیِ هدرِ پنل
              "flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-xl text-slate-300 transition hover:bg-slate-800 hover:text-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500"
            : "flex h-10 w-10 cursor-pointer items-center justify-center rounded-xl bg-slate-800/80 text-slate-200 transition hover:bg-slate-700 hover:text-brand-200 focus:outline-none focus:ring-2 focus:ring-brand-300/60"
        }
        aria-label={`تم پنل: ${current?.label || ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title="تم پنل"
      >
        {CurrentIcon ? <CurrentIcon className="h-[18px] w-[18px]" /> : null}
      </button>
      {open ? (
        <div role="menu" dir="rtl" className="absolute left-0 top-full z-[80] mt-2 w-52 overflow-hidden rounded-xl bg-slate-900 p-1 text-slate-100 shadow-xl shadow-black/40 ring-1 ring-slate-700">
          {OPTIONS.map(({ value, label, hint, Icon }) => (
            <button
              key={value}
              type="button"
              role="menuitemradio"
              aria-checked={theme === value}
              onClick={() => { applyTheme(value); setOpen(false); }}
              className={"flex w-full items-center gap-3 rounded-lg px-3 py-2 text-right transition hover:bg-slate-800 " + (theme === value ? "bg-slate-800/70" : "")}
            >
              <Icon className="h-4 w-4 shrink-0 text-slate-300" />
              <span className="min-w-0 flex-1">
                <span className="block text-[13px] font-semibold">{label}</span>
                <span className="block text-[11px] text-slate-400">{hint}</span>
              </span>
              {theme === value ? <Check className="h-4 w-4 shrink-0 text-brand-300" /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
